import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { createPortal } from 'react-dom';
import {
  Truck,
  ArrowLeft,
  Banknote,
  Edit3,
  Trash2,
  X,
  Save,
  AlertTriangle,
  Phone,
  Mail,
  MapPin,
  FileText,
  Calendar,
  RotateCcw,
  CreditCard,
  Search,
  CheckCircle2,
  Clock,
  Printer,
  TrendingDown,
  TrendingUp,
  FileSpreadsheet,
  AlertCircle,
  Package,
} from 'lucide-react';
import { clsx } from 'clsx';
import toast from 'react-hot-toast';
import api from '../config/api';
import SearchableSelect from '../components/SearchableSelect';
import PurchaseInvoice from '../components/PurchaseInvoice';
import { useSettings } from '../context/SettingsContext';
import PharmacyLogo from '../components/PharmacyLogo';
import { formatPakistanDate, formatPakistanDateTime } from '../utils/pakistanTime';

const avatarColors = [
  'bg-emerald-100 text-emerald-700',
  'bg-blue-100 text-blue-700',
  'bg-purple-100 text-purple-700',
  'bg-amber-100 text-amber-700',
  'bg-rose-100 text-rose-700',
  'bg-cyan-100 text-cyan-700',
];

function fmtDate(d) {
  if (!d) return '-';
  return formatPakistanDate(d, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function fmtDateTime(d) {
  if (!d) return '-';
  return formatPakistanDateTime(d, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function fmtMoney(amount) {
  const num = parseFloat(amount) || 0;
  return `Rs. ${num.toLocaleString('en-PK', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();

  const [supplier, setSupplier] = useState(null);
  const [loading, setLoading] = useState(true);
  const [purchases, setPurchases] = useState([]);
  const [payments, setPayments] = useState([]);
  const [activeTab, setActiveTab] = useState('purchases');

  // Modals
  const [showMakePayment, setShowMakePayment] = useState(false);
  const [showEditSupplier, setShowEditSupplier] = useState(false);
  const [showClearBalance, setShowClearBalance] = useState(false);
  const [showDeleteSupplier, setShowDeleteSupplier] = useState(false);
  const [deletePaymentItem, setDeletePaymentItem] = useState(null);
  const [viewPurchase, setViewPurchase] = useState(null);
  const [printPurchase, setPrintPurchase] = useState(null);

  // Forms
  const [paymentForm, setPaymentForm] = useState({
    amount: '',
    payment_date: new Date().toISOString().slice(0, 10),
    payment_method: 'cash',
    payment_type: 'paid',
    is_advance: false,
    notes: '',
  });

  const [editForm, setEditForm] = useState({});

  // Filters
  const [purchaseSearch, setPurchaseSearch] = useState('');
  const [purchaseStatusFilter, setPurchaseStatusFilter] = useState('all');

  useEffect(() => {
    if (id) {
      loadSupplierData();
    }
  }, [id]);

  const loadSupplierData = async () => {
    setLoading(true);
    try {
      const supId = parseInt(id);
      const supRes = await api.get(`/suppliers/${supId}`);
      setSupplier(supRes);

      // Load purchases for this supplier
      const purchasesRes = await api.get(`/purchases?limit=500`);
      const allPurchases = Array.isArray(purchasesRes) ? purchasesRes : (purchasesRes?.purchases || []);
      const supplierPurchases = allPurchases.filter((p) => p.supplier_id === supId && p.payment_method !== 'personal_account');
      setPurchases(supplierPurchases);

      // Load payments for this supplier
      const paymentsRes = await api.get(`/supplier-payments?supplier_id=${supId}&limit=500`);
      const paymentsList = Array.isArray(paymentsRes) ? paymentsRes : (paymentsRes?.payments || []);
      setPayments(paymentsList);
    } catch (err) {
      toast.error(err.message || 'Failed to load supplier details');
    } finally {
      setLoading(false);
    }
  };

  const openMakePaymentModal = (prefillAmount = null, isAdvance = false) => {
    const currentBal = Number(supplier?.balance || 0);
    const amount = prefillAmount !== null
      ? String(prefillAmount)
      : currentBal > 0
        ? String(currentBal)
        : '';
    setPaymentForm({
      amount,
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: 'cash',
      payment_type: 'paid',
      is_advance: isAdvance,
      notes: isAdvance ? 'Advance payment to supplier' : '',
    });
    setShowMakePayment(true);
  };

  const openRefundReceivedModal = (prefillAmount = null) => {
    const currentCredit = Number(supplier?.credit_balance || 0);
    const amount = prefillAmount !== null
      ? String(prefillAmount)
      : currentCredit > 0
        ? String(currentCredit)
        : '';
    setPaymentForm({
      amount,
      payment_date: new Date().toISOString().slice(0, 10),
      payment_method: 'cash',
      payment_type: 'refund_received',
      is_advance: false,
      notes: 'Advance refund received from supplier',
    });
    setShowMakePayment(true);
  };

  const handleMakePayment = async (e) => {
    e.preventDefault();
    const amount = parseFloat(paymentForm.amount);
    if (!amount || amount <= 0) {
      toast.error('Please enter a valid payment amount');
      return;
    }

    try {
      const isRefundReceived = paymentForm.payment_type === 'refund_received';
      const res = await api.post('/supplier-payments', {
        supplier_id: parseInt(id),
        payment_date: paymentForm.payment_date,
        payment_method: paymentForm.payment_method,
        payment_type: paymentForm.payment_type || 'paid',
        is_advance: paymentForm.is_advance || false,
        amount: amount,
        notes: paymentForm.notes,
      });

      toast.success(res?.message || (isRefundReceived ? `Advance refund of ${fmtMoney(amount)} received successfully!` : `Payment of ${fmtMoney(amount)} recorded successfully!`));
      setShowMakePayment(false);
      await loadSupplierData();
    } catch (err) {
      toast.error(err.message || 'Failed to record payment');
    }
  };

  const openEditModal = () => {
    setEditForm({
      name: supplier.name || '',
      phone: supplier.phone || '',
      email: supplier.email || '',
      address: supplier.address || '',
      balance: supplier.balance || '0',
      credit_balance: supplier.credit_balance || '0',
      notes: supplier.notes || '',
    });
    setShowEditSupplier(true);
  };

  const handleSaveSupplier = async (e) => {
    e.preventDefault();
    if (!editForm.name.trim()) {
      toast.error('Supplier name is required');
      return;
    }

    try {
      const updated = await api.put(`/suppliers/${supplier.id}`, {
        name: editForm.name,
        phone: editForm.phone,
        email: editForm.email,
        address: editForm.address,
        balance: parseFloat(editForm.balance) || 0,
        credit_balance: parseFloat(editForm.credit_balance) || 0,
        notes: editForm.notes,
      });

      toast.success('Supplier updated successfully');
      setSupplier(updated);
      setShowEditSupplier(false);
      await loadSupplierData();
    } catch (err) {
      toast.error(err.message || 'Failed to update supplier');
    }
  };

  const handleClearBalance = async () => {
    try {
      await api.put(`/suppliers/${supplier.id}/clear-balance`);
      toast.success('Balance cleared');
      setShowClearBalance(false);
      await loadSupplierData();
    } catch (err) {
      toast.error(err.message || 'Failed to clear balance');
    }
  };

  const handleDeleteSupplier = async () => {
    try {
      await api.delete(`/suppliers/${supplier.id}`);
      toast.success('Supplier deleted successfully');
      navigate('/suppliers');
    } catch (err) {
      toast.error(err.message || 'Failed to delete supplier');
    }
  };

  const handleDeletePayment = async () => {
    if (!deletePaymentItem) return;
    try {
      await api.delete(`/supplier-payments/${deletePaymentItem.id}`);
      toast.success('Payment deleted');
      setDeletePaymentItem(null);
      await loadSupplierData();
    } catch (err) {
      toast.error(err.message || 'Failed to delete payment');
    }
  };

  const openPurchaseDetails = async (purchase) => {
    setViewPurchase(purchase);
    try {
      const detailed = await api.get(`/purchases/${purchase.id}`);
      if (detailed) {
        setViewPurchase(detailed);
      }
    } catch {
      // Keep basic purchase info
    }
  };

  const handlePrintPurchase = async (pur) => {
    try {
      const detailed = await api.get(`/purchases/${pur.id}`);
      const data = detailed || pur;
      setPrintPurchase({
        ...data,
        supplier_name: supplier?.name || data.supplier_name,
        supplier_phone: supplier?.phone || data.supplier_phone,
        supplier_email: supplier?.email || data.supplier_email,
        supplier_address: supplier?.address || data.supplier_address,
        supplier_balance: supplier?.balance,
      });
    } catch {
      setPrintPurchase({
        ...pur,
        supplier_name: supplier?.name || pur.supplier_name,
        supplier_phone: supplier?.phone || pur.supplier_phone,
        supplier_email: supplier?.email || pur.supplier_email,
        supplier_address: supplier?.address || pur.supplier_address,
        supplier_balance: supplier?.balance,
      });
    }
  };

  if (loading && !supplier) {
    return (
      <div className="flex h-96 items-center justify-center">
        <div className="text-center">
          <div className="h-10 w-10 animate-spin rounded-full border-4 border-emerald-500 border-t-transparent mx-auto"></div>
          <p className="mt-3 text-sm text-gray-500 font-medium">Loading supplier details...</p>
        </div>
      </div>
    );
  }

  if (!supplier) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-3">
          <Link to="/suppliers" className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-600 hover:text-emerald-600">
            <ArrowLeft className="h-4 w-4" /> Back to Suppliers
          </Link>
        </div>
        <div className="rounded-2xl border border-gray-200 bg-white p-12 text-center shadow-sm">
          <AlertCircle className="mx-auto h-12 w-12 text-amber-500" />
          <h2 className="mt-3 text-lg font-bold text-gray-900">Supplier Not Found</h2>
          <p className="mt-1 text-sm text-gray-500">The requested supplier could not be found or may have been deleted.</p>
          <Link to="/suppliers" className="mt-5 inline-flex items-center gap-2 rounded-xl bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600">
            View All Suppliers
          </Link>
        </div>
      </div>
    );
  }

  // Calculations
  const balance = Number(supplier.balance || 0);
  const creditBalance = Number(supplier.credit_balance || 0);
  const weOwe = balance > 0 ? balance : 0;
  const theyOwe = creditBalance > 0 ? creditBalance : 0;

  const totalPurchased = purchases.reduce((sum, p) => sum + (parseFloat(p.total) || 0), 0);
  const totalDirectPayments = payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
  const totalPurchasePaid = purchases.reduce((sum, p) => sum + (parseFloat(p.paid_amount) || 0), 0);
  const unpaidPurchaseCount = purchases.filter((p) => p.payment_status === 'unpaid' || p.payment_status === 'partial').length;
  const unpaidPurchaseTotal = purchases
    .filter((p) => p.payment_status === 'unpaid' || p.payment_status === 'partial')
    .reduce((sum, p) => sum + Math.max(0, (parseFloat(p.total) || 0) - (parseFloat(p.paid_amount) || 0)), 0);

  // Filtered purchases
  const filteredPurchases = purchases.filter((p) => {
    const matchSearch = (p.reference_number || '').toLowerCase().includes(purchaseSearch.toLowerCase());
    const matchStatus = purchaseStatusFilter === 'all' || p.payment_status === purchaseStatusFilter;
    return matchSearch && matchStatus;
  });

  // Combined Ledger Entries
  const ledgerEntries = [
    ...purchases.map((p) => ({
      id: `purchase-${p.id}`,
      date: p.created_at || p.purchase_date,
      type: 'purchase',
      title: `Purchase #${p.reference_number}`,
      details: `${p.payment_method} purchase`,
      debit: parseFloat(p.total) || 0,
      credit: parseFloat(p.paid_amount) || 0,
      reference: p.reference_number,
      rawItem: p,
    })),
    ...payments.map((p) => {
      const isRefundRecv = p.payment_type === 'refund_received';
      return {
        id: `payment-${p.id}`,
        date: p.payment_date || p.created_at,
        type: isRefundRecv ? 'refund_received' : 'payment',
        title: isRefundRecv ? `Advance Refund Received (${p.payment_method})` : `Payment Made (${p.payment_method})`,
        details: p.notes || (isRefundRecv ? 'Supplier returned advance payment' : 'Payment to supplier'),
        debit: isRefundRecv ? (parseFloat(p.amount) || 0) : 0, // money came back from supplier
        credit: isRefundRecv ? 0 : (parseFloat(p.amount) || 0), // we paid supplier
        reference: isRefundRecv ? `SRF-${p.id}` : `SPY-${p.id}`,
        rawItem: p,
      };
    }),
  ];

  // Sort chronological for running balance calculation
  ledgerEntries.sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  let currentRunning = 0;
  const ledgerWithBalance = ledgerEntries.map((entry) => {
    currentRunning += entry.debit - entry.credit;
    return { ...entry, runningBalance: currentRunning };
  });

  // Reverse so newest is at top for display
  const displayLedger = [...ledgerWithBalance].reverse();

  const statusBadge = () => {
    if (weOwe > 0) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-red-50 border border-red-200 px-3 py-1 text-xs font-bold text-red-700">
          <AlertTriangle className="h-3.5 w-3.5 text-red-500" /> Pending {fmtMoney(weOwe)}
        </span>
      );
    }
    if (theyOwe > 0) {
      return (
        <span className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 border border-blue-200 px-3 py-1 text-xs font-bold text-blue-700">
          <TrendingDown className="h-3.5 w-3.5 text-blue-500" /> Advance {fmtMoney(theyOwe)}
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 border border-emerald-200 px-3 py-1 text-xs font-bold text-emerald-700">
        <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500" /> All Settled
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Breadcrumb / Top Bar */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/suppliers')}
            className="flex h-9 w-9 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-600 shadow-sm transition-colors hover:bg-gray-50 hover:text-gray-900"
            title="Back to Suppliers"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
          <div>
            <div className="flex items-center gap-2">
              <Link to="/suppliers" className="text-xs font-semibold text-gray-500 hover:text-emerald-600">
                Suppliers
              </Link>
              <span className="text-xs text-gray-400">/</span>
              <span className="text-xs font-semibold text-gray-900">{supplier.name}</span>
            </div>
            <h1 className="text-xl font-bold text-gray-900">Supplier Profile & Payments</h1>
          </div>
        </div>

        {/* Top Actions */}
        <div className="flex flex-wrap items-center gap-2.5">
          {theyOwe > 0 && (
            <button
              onClick={() => openRefundReceivedModal(theyOwe)}
              className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-blue-700 active:scale-[0.98]"
            >
              <RotateCcw className="h-4 w-4" /> Receive Refund ({fmtMoney(theyOwe)})
            </button>
          )}
          <button
            onClick={() => openMakePaymentModal()}
            className="flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2.5 text-sm font-semibold text-white shadow-sm transition-all hover:bg-emerald-600 active:scale-[0.98]"
          >
            <Banknote className="h-4 w-4" /> Make Payment
          </button>
          <button
            onClick={openEditModal}
            className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
          >
            <Edit3 className="h-4 w-4 text-gray-500" /> Edit
          </button>
          {weOwe > 0 && (
            <button
              onClick={() => setShowClearBalance(true)}
              className="flex items-center gap-1.5 rounded-xl border border-amber-200 bg-amber-50 px-3.5 py-2.5 text-sm font-semibold text-amber-800 shadow-sm transition-colors hover:bg-amber-100"
            >
              <RotateCcw className="h-4 w-4" /> Clear Dues
            </button>
          )}
          <button
            onClick={() => setShowDeleteSupplier(true)}
            className="flex items-center gap-1.5 rounded-xl border border-red-200 bg-white px-3 py-2.5 text-sm font-semibold text-red-600 shadow-sm transition-colors hover:bg-red-50"
            title="Delete Supplier"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Supplier Header Card */}
      <div className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex flex-col gap-6 md:flex-row md:items-start md:justify-between">
          <div className="flex items-start gap-4">
            <div className={clsx('flex h-16 w-16 flex-shrink-0 items-center justify-center rounded-2xl text-2xl font-bold', avatarColors[supplier.id % avatarColors.length])}>
              {supplier.name.charAt(0).toUpperCase()}
            </div>
            <div className="space-y-1.5">
              <div className="flex flex-wrap items-center gap-3">
                <h2 className="text-2xl font-bold text-gray-900">{supplier.name}</h2>
                {statusBadge()}
              </div>

              <div className="flex flex-wrap items-center gap-x-6 gap-y-2 text-sm text-gray-600">
                <div className="flex items-center gap-1.5">
                  <Phone className="h-4 w-4 text-gray-400" />
                  <span className="font-medium text-gray-800">{supplier.phone || 'No phone number'}</span>
                </div>
                {supplier.email && (
                  <div className="flex items-center gap-1.5">
                    <Mail className="h-4 w-4 text-gray-400" />
                    <span>{supplier.email}</span>
                  </div>
                )}
                {supplier.address && (
                  <div className="flex items-center gap-1.5">
                    <MapPin className="h-4 w-4 text-gray-400" />
                    <span>{supplier.address}</span>
                  </div>
                )}
              </div>

              {supplier.notes && (
                <div className="mt-2 rounded-lg bg-gray-50 border border-gray-100 px-3 py-2 text-xs text-gray-600 max-w-2xl">
                  <span className="font-semibold text-gray-700">Note: </span>
                  {supplier.notes}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col items-start md:items-end gap-2 border-t border-gray-100 pt-4 md:border-0 md:pt-0">
            <div className="text-left md:text-right">
              <p className="text-[11px] font-bold uppercase tracking-wider text-gray-400">Supplier Since</p>
              <p className="text-sm font-semibold text-gray-700">{fmtDate(supplier.created_at || new Date())}</p>
            </div>
          </div>
        </div>
      </div>

      {/* KPI Financial Cards */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {/* Outstanding Balance */}
        <div className={clsx(
          'rounded-2xl border p-5 shadow-sm transition-all',
          weOwe > 0 ? 'border-red-200 bg-red-50/50' : theyOwe > 0 ? 'border-blue-200 bg-blue-50/50' : 'border-gray-200 bg-white'
        )}>
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">
              {weOwe > 0 ? 'Pending' : theyOwe > 0 ? 'Advance' : 'Net Balance'}
            </p>
            <div className={clsx(
              'flex h-8 w-8 items-center justify-center rounded-xl',
              weOwe > 0 ? 'bg-red-100 text-red-600' : theyOwe > 0 ? 'bg-blue-100 text-blue-600' : 'bg-emerald-100 text-emerald-600'
            )}>
              <Banknote className="h-4 w-4" />
            </div>
          </div>
          <p className={clsx(
            'mt-2 text-2xl font-extrabold',
            weOwe > 0 ? 'text-red-600' : theyOwe > 0 ? 'text-blue-600' : 'text-emerald-600'
          )}>
            {fmtMoney(weOwe > 0 ? weOwe : theyOwe > 0 ? theyOwe : 0)}
          </p>
          <div className="mt-2 flex items-center justify-between text-xs">
            <span className="text-gray-500">
              {weOwe > 0 ? 'Pending dues to pay' : theyOwe > 0 ? 'Advance credit balance' : 'Zero balance'}
            </span>
            {weOwe > 0 && (
              <button
                onClick={() => openMakePaymentModal(weOwe)}
                className="font-bold text-red-600 hover:underline"
              >
                Pay Now &rarr;
              </button>
            )}
            {theyOwe > 0 && (
              <button
                onClick={() => openRefundReceivedModal(theyOwe)}
                className="font-bold text-blue-600 hover:underline"
              >
                Receive Refund &rarr;
              </button>
            )}
          </div>
        </div>

        {/* Total Purchased */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Purchased</p>
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-purple-100 text-purple-600">
              <Package className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-extrabold text-gray-900">{fmtMoney(totalPurchased)}</p>
          <p className="mt-2 text-xs text-gray-500">{purchases.length} total purchase{purchases.length === 1 ? '' : 's'}</p>
        </div>

        {/* Total Payments Made */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Total Payments</p>
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-emerald-100 text-emerald-600">
              <CreditCard className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-extrabold text-emerald-600">{fmtMoney(totalDirectPayments + totalPurchasePaid)}</p>
          <p className="mt-2 text-xs text-gray-500">{payments.length} direct payment{payments.length === 1 ? '' : 's'}</p>
        </div>

        {/* Pending Purchases */}
        <div className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="flex items-center justify-between">
            <p className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Unpaid Purchases</p>
            <div className="flex h-8 w-8 items-center justify-center rounded-xl bg-amber-100 text-amber-600">
              <Clock className="h-4 w-4" />
            </div>
          </div>
          <p className="mt-2 text-2xl font-extrabold text-amber-600">{unpaidPurchaseCount}</p>
          <p className="mt-2 text-xs text-gray-500">{fmtMoney(unpaidPurchaseTotal)} due on purchases</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 rounded-xl border border-gray-200 bg-gray-50 p-1">
        <button
          onClick={() => setActiveTab('purchases')}
          className={clsx(
            'flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
            activeTab === 'purchases' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <Package className="h-4 w-4" /> Purchase History ({purchases.length})
        </button>
        <button
          onClick={() => setActiveTab('payments')}
          className={clsx(
            'flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
            activeTab === 'payments' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <CreditCard className="h-4 w-4" /> Payments & Refunds ({payments.length})
        </button>
        <button
          onClick={() => setActiveTab('ledger')}
          className={clsx(
            'flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-semibold transition-all',
            activeTab === 'ledger' ? 'bg-white text-emerald-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'
          )}
        >
          <FileSpreadsheet className="h-4 w-4" /> Supplier Ledger Statement
        </button>
      </div>

      {/* TAB 1: PURCHASES */}
      {activeTab === 'purchases' && (
        <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
          <div className="flex flex-col gap-3 border-b border-gray-100 p-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search reference number..."
                value={purchaseSearch}
                onChange={(e) => setPurchaseSearch(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-9 pr-3 text-sm focus:border-emerald-500 focus:bg-white focus:outline-none"
              />
            </div>
            <div className="flex items-center gap-3">
              <SearchableSelect
                value={purchaseStatusFilter}
                onChange={(val) => setPurchaseStatusFilter(val)}
                options={[
                  { value: 'all', label: 'All Statuses' },
                  { value: 'paid', label: 'Paid' },
                  { value: 'partial', label: 'Partial' },
                  { value: 'unpaid', label: 'Unpaid' },
                ]}
                placeholder="Filter by Status"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm border-collapse">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                  <th className="sticky-th-left px-6 py-3 whitespace-nowrap">Reference #</th>
                  <th className="px-6 py-3 whitespace-nowrap">Date</th>
                  <th className="px-6 py-3 whitespace-nowrap">Status</th>
                  <th className="px-6 py-3 whitespace-nowrap text-right">Total Amount</th>
                  <th className="px-6 py-3 whitespace-nowrap text-right">Paid</th>
                  <th className="px-6 py-3 whitespace-nowrap text-right">Due Balance</th>
                  <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredPurchases.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                      No purchases found matching the criteria.
                    </td>
                  </tr>
                ) : (
                  filteredPurchases.map((p) => {
                    const total = parseFloat(p.total) || 0;
                    const paid = parseFloat(p.paid_amount) || 0;
                    const due = Math.max(0, total - paid);
                    return (
                      <tr key={p.id} className="group transition-colors hover:bg-gray-50/50">
                        <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                          <button
                            onClick={() => openPurchaseDetails(p)}
                            className="font-mono text-xs font-bold text-emerald-600 hover:underline"
                          >
                            {p.reference_number || `PUR-${p.id}`}
                          </button>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-600">{fmtDate(p.purchase_date || p.created_at)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span
                            className={clsx(
                              'inline-flex rounded-full px-2.5 py-1 text-[11px] font-semibold capitalize',
                              p.payment_status === 'paid'
                                ? 'bg-emerald-100 text-emerald-700'
                                : p.payment_status === 'partial'
                                  ? 'bg-amber-100 text-amber-700'
                                  : 'bg-red-100 text-red-700'
                            )}
                          >
                            {p.payment_status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900">{fmtMoney(total)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-emerald-600">{fmtMoney(paid)}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-bold text-red-600">{due > 0 ? fmtMoney(due) : '-'}</td>
                        <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                          <div className="flex items-center justify-end gap-1.5">
                            {due > 0 && (
                              <button
                                onClick={() => openMakePaymentModal(due)}
                                className="flex items-center gap-1 rounded-lg bg-emerald-50 border border-emerald-200 px-2 py-1 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-100"
                                title="Pay for this purchase"
                              >
                                <Banknote className="h-3.5 w-3.5" /> Pay
                              </button>
                            )}
                            <button
                              onClick={() => handlePrintPurchase(p)}
                              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-emerald-50 hover:text-emerald-600"
                              title="Print Purchase Order / Invoice"
                            >
                              <Printer className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => openPurchaseDetails(p)}
                              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-gray-100 hover:text-blue-600"
                              title="View Purchase Breakdown"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* TAB 2: PAYMENTS & REFUNDS */}
      {activeTab === 'payments' && (
        <div className="space-y-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h3 className="text-base font-bold text-gray-900">Payments & Refunds for {supplier.name}</h3>
              <p className="text-xs text-gray-500">Record payments made to supplier or receive advance refund returns.</p>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => openMakePaymentModal()}
                className="flex items-center gap-2 rounded-xl bg-emerald-500 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-emerald-600"
              >
                <Banknote className="h-4 w-4" /> Make Payment
              </button>
              <button
                onClick={() => openRefundReceivedModal(theyOwe > 0 ? theyOwe : null)}
                className="flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-xs font-semibold text-white shadow-sm hover:bg-blue-700"
              >
                <RotateCcw className="h-4 w-4" /> Receive Advance Refund
              </button>
            </div>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                    <th className="sticky-th-left px-6 py-3 whitespace-nowrap">Receipt / Date</th>
                    <th className="px-6 py-3 whitespace-nowrap">Type</th>
                    <th className="px-6 py-3 whitespace-nowrap">Payment Method</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right">Amount</th>
                    <th className="px-6 py-3 whitespace-nowrap">Notes & Reference</th>
                    <th className="px-6 py-3 whitespace-nowrap">Recorded At</th>
                    <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {payments.length === 0 ? (
                    <tr>
                      <td colSpan={7} className="px-6 py-12 text-center text-gray-400">
                        No payments recorded yet for this supplier.
                      </td>
                    </tr>
                  ) : (
                    payments.map((p) => {
                      const isRefundRecv = p.payment_type === 'refund_received';
                      return (
                        <tr key={p.id} className="group transition-colors hover:bg-gray-50/50">
                          <td className="sticky-col-left px-6 py-4 whitespace-nowrap">
                            <div className="font-semibold text-gray-900">{fmtDate(p.payment_date)}</div>
                            <div className="text-[11px] text-gray-400 font-mono">{isRefundRecv ? `SRF-${p.id}` : `SPY-${p.id}`}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {isRefundRecv ? (
                              <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-xs font-bold text-blue-700">
                                Refund Received
                              </span>
                            ) : (
                              <span className="inline-flex rounded-full bg-emerald-100 px-2.5 py-1 text-xs font-bold text-emerald-700">
                                Payment Made
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className="inline-flex rounded-full bg-blue-100 px-2.5 py-1 text-xs font-semibold capitalize text-blue-700">
                              {p.payment_method || 'cash'}
                            </span>
                          </td>
                          <td className={clsx('px-6 py-4 whitespace-nowrap text-right font-extrabold text-base', isRefundRecv ? 'text-blue-600' : 'text-emerald-600')}>
                            {isRefundRecv ? '+ ' : '- '}{fmtMoney(p.amount)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-600 text-xs max-w-xs truncate">
                            {p.notes || '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-gray-400 text-xs">
                            {fmtDateTime(p.created_at)}
                          </td>
                          <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right">
                            <button
                              onClick={() => setDeletePaymentItem(p)}
                              className="rounded-lg p-1.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                              title="Delete Payment"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* TAB 3: LEDGER */}
      {activeTab === 'ledger' && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-base font-bold text-gray-900">Statement of Account & Ledger</h3>
              <p className="text-xs text-gray-500">Chronological transaction audit trail with debit, credit, and running balance.</p>
            </div>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3.5 py-2 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50"
            >
              <Printer className="h-3.5 w-3.5" /> Print Statement
            </button>
          </div>

          <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left text-sm border-collapse">
                <thead>
                  <tr className="border-b border-gray-100 bg-gray-50/80 text-[10px] font-bold uppercase tracking-wider text-gray-500">
                    <th className="sticky-th-left px-6 py-3 whitespace-nowrap">Date</th>
                    <th className="px-6 py-3 whitespace-nowrap">Transaction</th>
                    <th className="px-6 py-3 whitespace-nowrap">Reference / Notes</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right">Debit (+)</th>
                    <th className="px-6 py-3 whitespace-nowrap text-right">Credit (-)</th>
                    <th className="sticky-th-right px-6 py-3 whitespace-nowrap text-right">Running Balance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {displayLedger.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-6 py-12 text-center text-gray-400">
                        No transactions recorded for this supplier.
                      </td>
                    </tr>
                  ) : (
                    displayLedger.map((entry) => (
                      <tr key={entry.id} className="group transition-colors hover:bg-gray-50/50">
                        <td className="sticky-col-left px-6 py-4 whitespace-nowrap text-xs text-gray-600">{fmtDate(entry.date)}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <span
                              className={clsx(
                                'flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold',
                                entry.type === 'purchase' ? 'bg-purple-100 text-purple-700' : entry.type === 'refund_received' ? 'bg-blue-100 text-blue-700' : 'bg-emerald-100 text-emerald-700'
                              )}
                            >
                              {entry.type === 'purchase' ? 'PUR' : entry.type === 'refund_received' ? 'REF' : 'PAY'}
                            </span>
                            <span className="font-semibold text-gray-900 text-xs">{entry.title}</span>
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-xs text-gray-500">{entry.details}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-gray-900">
                          {entry.debit > 0 ? fmtMoney(entry.debit) : '-'}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right font-medium text-emerald-600">
                          {entry.credit > 0 ? fmtMoney(entry.credit) : '-'}
                        </td>
                        <td className="sticky-col-right px-6 py-4 whitespace-nowrap text-right font-extrabold text-gray-900">
                          {fmtMoney(entry.runningBalance)}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* MAKE PAYMENT / RECEIVE REFUND MODAL */}
      {showMakePayment &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowMakePayment(false)} />
            <div className="relative w-full max-w-md rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">
                    {paymentForm.payment_type === 'refund_received' ? 'Receive Advance Refund from Supplier' : 'Make Supplier Payment'}
                  </h3>
                  <p className="text-xs text-gray-500">
                    {paymentForm.payment_type === 'refund_received'
                      ? `Record refund returned by ${supplier.name} into your account`
                      : `Record payment made to ${supplier.name}`}
                  </p>
                </div>
                <button
                  onClick={() => setShowMakePayment(false)}
                  className="rounded-lg p-2 text-gray-400 hover:bg-gray-100"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Type selector */}
              <div className="mb-4 flex rounded-xl bg-gray-100 p-1">
                <button
                  type="button"
                  onClick={() => setPaymentForm({
                    ...paymentForm,
                    payment_type: 'paid',
                    amount: weOwe > 0 ? String(weOwe) : '',
                    notes: paymentForm.notes === 'Advance refund received from supplier' ? '' : paymentForm.notes,
                  })}
                  className={clsx(
                    'flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5',
                    paymentForm.payment_type === 'paid'
                      ? 'bg-emerald-500 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <Banknote className="h-3.5 w-3.5" /> Pay Supplier (Cash Out)
                </button>
                <button
                  type="button"
                  onClick={() => setPaymentForm({
                    ...paymentForm,
                    payment_type: 'refund_received',
                    amount: theyOwe > 0 ? String(theyOwe) : '',
                    notes: !paymentForm.notes ? 'Advance refund received from supplier' : paymentForm.notes,
                  })}
                  className={clsx(
                    'flex-1 py-2 text-xs font-bold rounded-lg transition-all flex items-center justify-center gap-1.5',
                    paymentForm.payment_type === 'refund_received'
                      ? 'bg-blue-600 text-white shadow-sm'
                      : 'text-gray-600 hover:text-gray-900'
                  )}
                >
                  <RotateCcw className="h-3.5 w-3.5" /> Receive Refund (Cash In)
                </button>
              </div>

              {/* Status banner */}
              {paymentForm.payment_type === 'refund_received' ? (
                <div className="mb-4 rounded-xl border border-blue-200 bg-blue-50 p-3 text-xs text-blue-800 space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Supplier Advance Credit:</span>
                    <span className="text-sm font-extrabold">{fmtMoney(theyOwe)}</span>
                  </div>
                  <p className="text-[11px] text-blue-700">
                    This refund receipt will <strong>add money to your Cash / Bank balance</strong> in accounting and reduce what the supplier owes you.
                  </p>
                </div>
              ) : (
                <div className={clsx(
                  'mb-4 rounded-xl border p-3 text-xs',
                  weOwe > 0 ? 'border-red-200 bg-red-50 text-red-800' : 'border-emerald-200 bg-emerald-50 text-emerald-800'
                )}>
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Current Pending Dues:</span>
                    <span className="text-sm font-extrabold">{fmtMoney(weOwe)}</span>
                  </div>
                </div>
              )}

              <form onSubmit={handleMakePayment} className="space-y-4">
                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    {paymentForm.payment_type === 'refund_received' ? 'Refund Amount Received (Rs.) *' : 'Payment Amount (Rs.) *'}
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    min="0.01"
                    required
                    value={paymentForm.amount}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amount: e.target.value })}
                    placeholder="Enter amount (Rs.)"
                    className="w-full rounded-xl border border-gray-200 bg-gray-50 px-3.5 py-2.5 text-base font-bold text-gray-900 focus:border-emerald-500 focus:bg-white focus:outline-none"
                    autoFocus
                  />
                  {/* Quick pre-set amount chips */}
                  {paymentForm.payment_type === 'refund_received' && theyOwe > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => setPaymentForm({ ...paymentForm, amount: String(theyOwe) })}
                        className="rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-[11px] font-semibold text-blue-700 hover:bg-blue-100"
                      >
                        Full Advance ({fmtMoney(theyOwe)})
                      </button>
                    </div>
                  )}
                  {paymentForm.payment_type === 'paid' && weOwe > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      <button
                        type="button"
                        onClick={() => setPaymentForm({ ...paymentForm, amount: String(weOwe) })}
                        className="rounded-md border border-gray-200 bg-white px-2 py-1 text-[11px] font-semibold text-gray-700 hover:bg-gray-100"
                      >
                        Full Dues ({fmtMoney(weOwe)})
                      </button>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                      Payment Date
                    </label>
                    <input
                      type="date"
                      value={paymentForm.payment_date}
                      onChange={(e) => setPaymentForm({ ...paymentForm, payment_date: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                      Payment Method
                    </label>
                    <div className="flex gap-2">
                      {['cash', 'card'].map((m) => (
                        <button
                          type="button"
                          key={m}
                          onClick={() => setPaymentForm({ ...paymentForm, payment_method: m })}
                          className={clsx(
                            'flex-1 py-2 text-xs font-semibold rounded-lg border transition-colors capitalize',
                            paymentForm.payment_method === m
                              ? paymentForm.payment_type === 'refund_received'
                                ? 'bg-blue-600 text-white border-blue-600'
                                : 'bg-emerald-500 text-white border-emerald-500'
                              : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-100'
                          )}
                        >
                          {m}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div>
                  <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">
                    Notes / Reference
                  </label>
                  <textarea
                    rows={2}
                    value={paymentForm.notes}
                    onChange={(e) => setPaymentForm({ ...paymentForm, notes: e.target.value })}
                    placeholder={paymentForm.payment_type === 'refund_received' ? 'Reason / notes for refund received...' : 'e.g. Receipt #, Bank txn ref, Cheque #'}
                    className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                  />
                </div>

                <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowMakePayment(false)}
                    className="rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className={clsx(
                      'flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-semibold text-white shadow-sm active:scale-[0.98]',
                      paymentForm.payment_type === 'refund_received'
                        ? 'bg-blue-600 hover:bg-blue-700'
                        : 'bg-emerald-500 hover:bg-emerald-600'
                    )}
                  >
                    {paymentForm.payment_type === 'refund_received' ? (
                      <><RotateCcw className="h-4 w-4" /> Receive Refund & Deposit to Account</>
                    ) : (
                      <><Banknote className="h-4 w-4" /> Save Payment</>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* EDIT SUPPLIER MODAL */}
      {showEditSupplier &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowEditSupplier(false)} />
            <div className="relative w-full max-w-lg rounded-2xl border border-gray-200 bg-white shadow-2xl">
              <div className="flex items-center justify-between border-b border-gray-100 px-6 py-4">
                <h3 className="text-lg font-semibold text-gray-900">Edit Supplier Profile</h3>
                <button onClick={() => setShowEditSupplier(false)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleSaveSupplier} className="px-6 py-5">
                <div className="grid grid-cols-2 gap-4">
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Full Name *</label>
                    <input
                      value={editForm.name || ''}
                      onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                      required
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Phone</label>
                    <input
                      value={editForm.phone || ''}
                      onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>

                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Pending (Rs.)</label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.balance || '0'}
                      onChange={(e) => setEditForm({ ...editForm, balance: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div>
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Advance (Rs.)</label>
                    <input
                      type="number"
                      min="0"
                      value={editForm.credit_balance || '0'}
                      onChange={(e) => setEditForm({ ...editForm, credit_balance: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Address</label>
                    <input
                      value={editForm.address || ''}
                      onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="mb-1 block text-[11px] font-bold uppercase tracking-wider text-gray-500">Note</label>
                    <textarea
                      value={editForm.notes || ''}
                      onChange={(e) => setEditForm({ ...editForm, notes: e.target.value })}
                      rows={2}
                      className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2.5 text-sm focus:border-emerald-500 focus:outline-none resize-none"
                    />
                  </div>
                </div>
                <div className="mt-5 flex justify-end gap-3 border-t border-gray-100 pt-4">
                  <button
                    type="button"
                    onClick={() => setShowEditSupplier(false)}
                    className="rounded-lg border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="flex items-center gap-2 rounded-lg bg-emerald-500 px-5 py-2.5 text-sm font-semibold text-white hover:bg-emerald-600"
                  >
                    <Save className="h-4 w-4" /> Save Changes
                  </button>
                </div>
              </form>
            </div>
          </div>,
          document.body
        )}

      {/* CLEAR BALANCE MODAL */}
      {showClearBalance &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowClearBalance(false)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-amber-100">
                <RotateCcw className="h-7 w-7 text-amber-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Clear Supplier Balance?</h3>
              <p className="mt-2 text-sm text-gray-500">
                This will reset <strong>{supplier.name}</strong>'s outstanding balance of <strong>{fmtMoney(weOwe)}</strong> to <strong>Rs. 0.00</strong>.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <button
                  onClick={() => setShowClearBalance(false)}
                  className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleClearBalance}
                  className="rounded-lg bg-amber-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-amber-700"
                >
                  Clear Balance
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* DELETE SUPPLIER CONFIRM MODAL */}
      {showDeleteSupplier &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setShowDeleteSupplier(false)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Supplier?</h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete <strong>{supplier.name}</strong>? This action cannot be undone.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <button
                  onClick={() => setShowDeleteSupplier(false)}
                  className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteSupplier}
                  className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* DELETE PAYMENT CONFIRM MODAL */}
      {deletePaymentItem &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setDeletePaymentItem(null)} />
            <div className="relative w-full max-w-sm rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl text-center">
              <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-red-100">
                <AlertTriangle className="h-7 w-7 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900">Delete Payment?</h3>
              <p className="mt-2 text-sm text-gray-500">
                Are you sure you want to delete this payment of <strong>{fmtMoney(deletePaymentItem.amount)}</strong>? The supplier's balance will be adjusted accordingly.
              </p>
              <div className="mt-6 flex justify-center gap-3">
                <button
                  onClick={() => setDeletePaymentItem(null)}
                  className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeletePayment}
                  className="rounded-lg bg-red-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-red-700"
                >
                  Delete Payment
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* VIEW PURCHASE DETAIL MODAL */}
      {viewPurchase &&
        createPortal(
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div className="fixed inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setViewPurchase(null)} />
            <div className="relative max-h-[90vh] w-full max-w-xl overflow-y-auto rounded-2xl border border-gray-200 bg-white p-6 shadow-2xl">
              <div className="mb-4 flex items-center justify-between border-b border-gray-100 pb-4">
                <div>
                  <h3 className="text-lg font-bold text-gray-900">Purchase {viewPurchase.reference_number}</h3>
                  <p className="text-xs text-gray-500">{fmtDateTime(viewPurchase.created_at)}</p>
                </div>
                <button onClick={() => setViewPurchase(null)} className="rounded-lg p-2 text-gray-400 hover:bg-gray-100">
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Items Breakdown */}
              {viewPurchase.items && viewPurchase.items.length > 0 && (
                <div className="mb-4 rounded-xl border border-gray-100 bg-gray-50/50 p-3">
                  <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-gray-600">Purchased Items</h4>
                  <table className="w-full text-left text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 text-gray-500">
                        <th className="pb-1">Item</th>
                        <th className="pb-1 text-center">Qty</th>
                        <th className="pb-1 text-right">Buy Price</th>
                        <th className="pb-1 text-right">Total</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {viewPurchase.items.map((it, idx) => (
                        <tr key={idx} className="text-gray-700">
                          <td className="py-1.5 font-medium">{it.medicine_name || `Medicine #${it.medicine_id}`}</td>
                          <td className="py-1.5 text-center">{it.quantity}</td>
                          <td className="py-1.5 text-right">{fmtMoney(it.buy_price)}</td>
                          <td className="py-1.5 text-right font-semibold text-gray-900">{fmtMoney(it.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              <div className="space-y-2.5 text-sm">
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Supplier</span>
                  <span className="font-semibold text-gray-900">{supplier.name}</span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Subtotal</span>
                  <span className="text-gray-900">{fmtMoney(viewPurchase.subtotal)}</span>
                </div>
                {Number(viewPurchase.discount) > 0 && (
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-500">Discount</span>
                    <span className="text-red-500">- {fmtMoney(viewPurchase.discount)}</span>
                  </div>
                )}
                {Number(viewPurchase.tax) > 0 && (
                  <div className="flex justify-between border-b border-gray-100 pb-2">
                    <span className="text-gray-500">Tax</span>
                    <span className="text-gray-900">{fmtMoney(viewPurchase.tax)}</span>
                  </div>
                )}
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="font-bold text-gray-900">Grand Total</span>
                  <span className="text-lg font-extrabold text-emerald-600">{fmtMoney(viewPurchase.total)}</span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Paid Amount</span>
                  <span className="font-semibold text-gray-900">{fmtMoney(viewPurchase.paid_amount)}</span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Balance Due</span>
                  <span className={clsx('font-bold', (parseFloat(viewPurchase.total) - parseFloat(viewPurchase.paid_amount || 0)) > 0 ? 'text-red-600' : 'text-gray-700')}>
                    {fmtMoney(Math.max(0, parseFloat(viewPurchase.total) - parseFloat(viewPurchase.paid_amount || 0)))}
                  </span>
                </div>
                <div className="flex justify-between border-b border-gray-100 pb-2">
                  <span className="text-gray-500">Payment Method</span>
                  <span className="capitalize text-gray-900">{viewPurchase.payment_method}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Status</span>
                  <span
                    className={clsx(
                      'font-semibold capitalize',
                      viewPurchase.payment_status === 'paid'
                        ? 'text-emerald-600'
                        : viewPurchase.payment_status === 'partial'
                          ? 'text-amber-600'
                          : 'text-red-600'
                    )}
                  >
                    {viewPurchase.payment_status}
                  </span>
                </div>
              </div>

              <div className="mt-6 flex justify-end gap-3 border-t border-gray-100 pt-4">
                <button
                  type="button"
                  onClick={() => setViewPurchase(null)}
                  className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
                >
                  Close
                </button>
                <button
                  type="button"
                  onClick={() => handlePrintPurchase(viewPurchase)}
                  className="flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 active:scale-95 transition-all"
                >
                  <Printer className="h-4 w-4" /> Print Purchase
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}

      {/* ══════ PURCHASE INVOICE MODAL & PRINT PORTAL ══════ */}
      {printPurchase &&
        createPortal(
          <PurchaseInvoice
            purchase={printPurchase}
            onClose={() => setPrintPurchase(null)}
          />,
          document.body
        )}

      {/* ══════ DEDICATED PRINT CONTAINER FOR SUPPLIER STATEMENT ══════ */}
      {activeTab === 'ledger' &&
        createPortal(
          <div id="printable-statement-export" className="hidden print:block" style={{ fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', padding: 30, color: '#1a1a1a' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, borderBottom: '3px solid #10b981', paddingBottom: 16 }}>
              <div>
                <PharmacyLogo logo={settings?.pharmacy_logo} alt="Shop logo" className="h-10 w-10 rounded-lg mb-2" imageClassName="h-10 w-10 rounded-lg object-contain mb-2" />
                <h1 style={{ fontSize: 22, color: '#10b981', margin: 0, fontWeight: 800 }}>{settings?.pharmacy_name || 'Gaza Pharmacy'}</h1>
                <p style={{ fontSize: 13, color: '#6b7280', margin: '2px 0 0' }}>Supplier Statement & Ledger</p>
                {settings?.pharmacy_address && <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>{settings.pharmacy_address}</p>}
                {settings?.pharmacy_phone && <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Tel: {settings.pharmacy_phone}</p>}
              </div>
              <div style={{ textAlign: 'right' }}>
                <p style={{ fontSize: 14, fontWeight: 700, margin: 0 }}>{supplier?.name}</p>
                {supplier?.phone && <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Phone: {supplier.phone}</p>}
                {supplier?.email && <p style={{ fontSize: 11, color: '#6b7280', margin: '2px 0 0' }}>Email: {supplier.email}</p>}
                <p style={{ fontSize: 11, color: '#6b7280', margin: '4px 0 0' }}>Generated: {formatPakistanDateTime(new Date())}</p>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 12, marginBottom: 20 }}>
              <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Purchases</p>
                <p style={{ fontSize: 16, fontWeight: 700, margin: '4px 0 0' }}>{fmtMoney(totalPurchased)}</p>
              </div>
              <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Total Paid</p>
                <p style={{ fontSize: 16, fontWeight: 700, margin: '4px 0 0', color: '#059669' }}>{fmtMoney(totalDirectPayments + totalPurchasePaid)}</p>
              </div>
              <div style={{ flex: 1, padding: 12, border: '1px solid #e5e7eb', borderRadius: 8, background: '#f9fafb' }}>
                <p style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', color: '#6b7280', margin: 0 }}>Current Balance Due</p>
                <p style={{ fontSize: 16, fontWeight: 700, margin: '4px 0 0', color: (supplier?.balance || 0) > 0 ? '#dc2626' : '#111827' }}>{fmtMoney(supplier?.balance || 0)}</p>
              </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11, marginTop: 16 }}>
              <thead>
                <tr style={{ background: '#f3f4f6', borderBottom: '2px solid #e5e7eb', textAlign: 'left' }}>
                  <th style={{ padding: '8px 10px' }}>Date</th>
                  <th style={{ padding: '8px 10px' }}>Transaction</th>
                  <th style={{ padding: '8px 10px' }}>Reference / Details</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Debit (+)</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Credit (-)</th>
                  <th style={{ padding: '8px 10px', textAlign: 'right' }}>Balance</th>
                </tr>
              </thead>
              <tbody>
                {displayLedger.map((entry, i) => (
                  <tr key={i} style={{ borderBottom: '1px solid #e5e7eb' }}>
                    <td style={{ padding: '8px 10px', color: '#4b5563' }}>{fmtDate(entry.date)}</td>
                    <td style={{ padding: '8px 10px', fontWeight: 600 }}>{entry.title}</td>
                    <td style={{ padding: '8px 10px', color: '#6b7280' }}>{entry.details}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600 }}>{entry.debit > 0 ? fmtMoney(entry.debit) : '-'}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 600, color: '#059669' }}>{entry.credit > 0 ? fmtMoney(entry.credit) : '-'}</td>
                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: 700 }}>{fmtMoney(entry.runningBalance)}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div style={{ marginTop: 24, textAlign: 'center', fontSize: 10, color: '#9ca3af', borderTop: '1px solid #e5e7eb', paddingTop: 12 }}>
              {settings?.pharmacy_name || 'Gaza Pharmacy'} Management System — Statement Generated on {formatPakistanDateTime(new Date())}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
